/* eslint-disable indent */
import { CheckpointTypeFilterType } from '@/entities/checkpoint';
import { EquipmentDmgFilterType } from '@/entities/equipment';
import { StructureAdvancedFilter } from '@/entities/structure/group';
import { UserFilterType } from '@/entities/user';
import { VehiculeCheckFilterType } from '@/entities/vehicle';
import { Equipment } from '@/api/entities/equipment';
import { DATE_DD_MM_YYYY_REGEX, DATE_YYYY_MM_DD_REGEX, EMAIL_REGEX } from './regex.utils';

export const mapStructureFilterAPI = (filter: StructureAdvancedFilter) =>
  `key=${filter.key}${
    filter.isDistributor ? '&isDistributor=' + filter.isDistributor : ''
  }${filter.isBranch ? '&isBranch=' + filter.isBranch : ''}${
    filter.isWhiteMark ? '&isWhiteMark=' + filter.isWhiteMark : ''
  }${filter.startDate ? '&startDate=' + filter.startDate : ''}${
    filter.endDate ? '&endDate=' + filter.endDate : ''
  }${filter.groupId ? '&groupId=' + filter.groupId : ''}${
    filter.distributorId ? '&distributorId=' + filter.distributorId : ''
  }${filter.companyId ? '&companyId=' + filter.companyId : ''}${
    filter.role ? '&role=' + filter.role : ''
  }${filter.userId ? '&userId=' + filter.userId : ''}
  ${filter.branchId ? '&branchId=' + filter.branchId : ''}`;

export const mapFilterSelect = (data) => {
  const mappedData = Array.isArray(data)
    ? data.map((item) => {
        const clientValue =
          item?.firstname && item?.lastname
            ? `${item?.firstname}_separator_${item?.lastname}`
            : item?.firstname || item?.lastname || item?.email;

        const clientLabel =
          item?.firstname && item?.lastname
            ? `${item?.firstname} ${item?.lastname?.toUpperCase()}`
            : item?.firstname || item?.lastname || item?.email;

        return {
          value: item?.id || clientValue,
          label: item?.name || clientLabel,
        };
      })
    : [];

  return mappedData;
};

export const mapUserFilterAPI = (filter: UserFilterType) =>
  `key=${filter.key}${filter.role ? '&role=' + filter.role : ''}${
    filter.status ? '&status=' + filter.status : ''
  }${filter.groupId ? '&groupId=' + filter.groupId : ''}${
    filter.companyId ? '&companyId=' + filter.companyId : ''
  }${filter.branchId ? '&branchId=' + filter.branchId : ''}${
    filter.userId ? '&userId=' + filter.userId : ''
  }${filter.roleFilter ? '&roleFilter=' + filter.roleFilter : ''}`;

export const mapVehicleFilterAPI = (filter: VehiculeCheckFilterType) =>
  `key=${filter.key ?? ''}${filter.role ? '&role=' + filter.role : ''}${
    filter.userId ? '&userId=' + filter.userId : ''
  }${filter.vehiculeId ? '&vehiculeId=' + filter.vehiculeId : ''}${
    filter.actionUserId ? '&actionUserId=' + filter.actionUserId : ''
  }${filter.status ? '&status=' + filter.status : ''}${
    filter.type ? '&type=' + filter.type : ''
  }${filter.startDate ? '&startDate=' + filter.startDate : ''}${
    filter.endDate ? '&endDate=' + filter.endDate : ''
  }${filter.groupId ? '&groupId=' + filter.groupId : ''}${
    filter.companyId ? '&companyId=' + filter.companyId : ''
  }${filter.branchId ? '&branchId=' + filter.branchId : ''}${
    filter.requestInspection
      ? '&requestInspection=' + filter.requestInspection
      : ''
  }`;

export const mapEquipmentFilterAPI = (filter: EquipmentDmgFilterType) =>
  `key=${filter.key ?? ''}${filter.status ? '&status=' + filter.status : ''}${
    filter.isMultiple ? '&isMultiple=' + filter.isMultiple : ''
  }${filter.sortBy ? '&sortColumn=' + filter.sortBy : ''}
  ${filter.sortDirection ? '&sortDirection=' + filter.sortDirection : ''}`;

export const mapDmgFilterAPI = (filter: EquipmentDmgFilterType) =>
  `key=${filter.key ?? ''}${filter.status ? '&status=' + filter.status : ''}`;

export const mapCheckpointTypeFilterAPI = (filter: CheckpointTypeFilterType) =>
  `key=${filter.key ?? ''}${filter.status ? '&status=' + filter.status : ''}${
    filter.type ? '&type=' + filter.type : ''
  }`;

export const mapVehiclesImport = (vehicles: object[], vehiculeOwner) => {
  const mappedVehicles = vehicles.map((item: Record<string, unknown>) => {
    const normalized = Object.fromEntries(
      Object.entries(item).map(([key, value]) => [
        key.toLowerCase().replace(/\s/g, ''),
        value,
      ]),
    );
    return {
      brand: normalized['brand']?.toString(),
      model: normalized['model']?.toString(),
      vin: normalized['vin']?.toString() || null,
      licensePlateNumber: normalized['licenseplatenumber']?.toString() || null,
      yearOfFirstRegistration: normalized['yearoffirstregistration'] || null,
      mileage: normalized['mileage'] || null,
      comment: normalized['comment']?.toString() || null,
      picture: normalized['picture']?.toString() || null,
      ownerType: vehiculeOwner?.ownerType,
      ownerGroupId: vehiculeOwner?.ownerGroupId,
      ownerCompanyId: vehiculeOwner?.ownerCompanyId,
    };
  });

  const hasMissingBrand = mappedVehicles?.some((v) => !v?.brand);
  const hasMissingModel = mappedVehicles?.some((v) => !v?.model);

  const licensePlates = mappedVehicles
    ?.map((v) => v?.licensePlateNumber)
    .filter(Boolean);
  const hasDuplicateLicensePlateNumber =
    licensePlates.length !== new Set(licensePlates).size;

  const vins = mappedVehicles?.map((v) => v?.vin).filter(Boolean);
  const hasDuplicateVin = vins.length !== new Set(vins).size;

  const hasInvalidYear = mappedVehicles?.some((v) => {
    const val = v?.yearOfFirstRegistration;
    if (val === undefined || val === null || val === '') return false;
    const num = Number(val);
    return (
      !Number.isInteger(num) || num < 1900 || num > new Date().getFullYear()
    );
  });

  const hasInvalidMileage = mappedVehicles?.some((v) => {
    const val = v?.mileage;
    if (val === undefined || val === null || val === '') return false;
    const num = Number(val);
    return isNaN(num) || num < 0;
  });

  return {
    vehicles: mappedVehicles,
    hasMissingBrand,
    hasMissingModel,
    hasDuplicateLicensePlateNumber,
    hasDuplicateVin,
    hasInvalidYear,
    hasInvalidMileage,
  };
};

export const mapVehicleCheckRequestsImport = (rows: object[]) => {
  let hasInvalidDate = false;
  const mappedItems = rows.map((item: Record<string, unknown>) => {
    const normalized = Object.fromEntries(
      Object.entries(item).map(([key, value]) => [
        key.toLowerCase().replace(/\s/g, ''),
        value,
      ]),
    );

    const additionalEmailsRaw = (
      normalized['additionalemails']?.toString() || ''
    )
      .trim()
      .replace(/^\[|\]$/g, '');
    const additionalEmails = additionalEmailsRaw
      ? additionalEmailsRaw
          .split(/[,;]/)
          .map((e) => e.trim())
          .filter(Boolean)
      : [];

    const rawDateValue = normalized['inspectionstartdate'];
    // XLSX returns Excel date cells as JS Date objects — convert to yyyy-MM-dd before regex validation
    const rawDate: string =
      rawDateValue instanceof Date
        ? `${rawDateValue.getFullYear()}-${String(rawDateValue.getMonth() + 1).padStart(2, '0')}-${String(rawDateValue.getDate()).padStart(2, '0')}`
        : rawDateValue?.toString().trim() || '';
    if (
      rawDate &&
      !DATE_DD_MM_YYYY_REGEX.test(rawDate) &&
      !DATE_YYYY_MM_DD_REGEX.test(rawDate)
    ) {
      hasInvalidDate = true;
    }

    return {
      startDate: (() => {
        if (!rawDate) return new Date().toISOString().slice(0, 10);
        const ddMMyyyy = rawDate.match(DATE_DD_MM_YYYY_REGEX);
        if (ddMMyyyy) return `${ddMMyyyy[3]}-${ddMMyyyy[2]}-${ddMMyyyy[1]}`;
        const yyyyMMdd = rawDate.match(DATE_YYYY_MM_DD_REGEX);
        if (yyyyMMdd) return `${yyyyMMdd[1]}-${yyyyMMdd[2]}-${yyyyMMdd[3]}`;
        return rawDate;
      })(),
      vin: normalized['vin']?.toString() || null,
      licensePlateNumber: normalized['licenseplatenumber']?.toString() || null,
      brand: normalized['brand']?.toString() || null,
      model: normalized['model']?.toString() || null,
      individualFirstname: normalized['firstname']?.toString() || null,
      individualLastname: normalized['lastname']?.toString() || null,
      individualEmail: normalized['email']?.toString() || null,
      additionalEmails,
      individualPhone: normalized['phone']?.toString() || null,
      individualAddressLine1: normalized['addressline1']?.toString() || null,
      individualAddressLine2: normalized['addressline2']?.toString() || null,
      individualPostalCode: normalized['postalcode']?.toString() || null,
      individualCity: normalized['city']?.toString() || null,
      individualCountry: normalized['country']?.toString() || null,
    };
  });

  const hasMissingVehicleIdentifier = mappedItems.some(
    (item) => !item.brand || !item.model,
  );
  const hasMissingFirstName = mappedItems.some(
    (item) => !item.individualFirstname,
  );
  const hasMissingLastName = mappedItems.some(
    (item) => !item.individualLastname,
  );
  const hasMissingEmail = mappedItems.some((item) => !item.individualEmail);

  const hasInvalidEmail = mappedItems.some((item) => {
    if (item.individualEmail && !EMAIL_REGEX.test(item.individualEmail))
      return true;
    return item.additionalEmails.some(
      (email) => email && !EMAIL_REGEX.test(email),
    );
  });

  return {
    items: mappedItems,
    hasInvalidDate,
    hasMissingVehicleIdentifier,
    hasMissingFirstName,
    hasMissingLastName,
    hasMissingEmail,
    hasInvalidEmail,
  };
};

export const mapEquipmentOptions = (
  equipments: Equipment[] | null | undefined,
) => {
  if (!Array.isArray(equipments)) {
    return [{ label: 'Aucune option disponible', id: 'none' }];
  }
  return [
    { label: 'Pas de translation automatique', id: 'none' },
    ...equipments.map((item) => ({
      label: item.name,
      id: item.id,
    })),
  ];
};

export const mapEquipmentInfosDefaultValues = (
  data: Equipment,
  translatedEquipment: Equipment,
) => {
  return {
    ...(data as Equipment),
    name: translatedEquipment?.name || data?.name || '',
    comment: translatedEquipment?.comment || data?.comment || '',
    isFrontPart: data?.isFrontPart || false,
    isBackPart: data?.isBackPart || false,
    isLeftPart: data?.isLeftPart || false,
    isRightPart: data?.isRightPart || false,
    frontPartTranslation: data?.frontPartTranslation || 'none',
    backPartTranslation: data?.backPartTranslation || 'none',
    leftPartTranslation: data?.leftPartTranslation || 'none',
    rightPartTranslation: data?.rightPartTranslation || 'none',
  };
};
